<template>
  <div id="dashboard">
    <div class="mb-3">
      <ReportHeader
        title="Dashboard"
        period-filter
        group-filter
        team-filter
        @filterChanged="getData()"
      />
    </div>
    <div class="row">
      <DashboardPanel
        :colour="state.total.colour"
        :count="state.total.count"
        :title="group.name + ' Total'"
      >
        <SigmaIcon title="" />
      </DashboardPanel>

      <DashboardPanel
        :colour="state.new.colour"
        :count="state.new.count"
        :title="group.name + ' New'"
      >
        <ClockStartIcon title="" />
      </DashboardPanel>

      <DashboardPanel
        :colour="state.open.colour"
        :count="state.open.count"
        :title="group.name + ' Open'"
      >
        <ClockFastIcon title="" />
      </DashboardPanel>

      <DashboardPanel
        :colour="state.closed.colour"
        :count="state.closed.count"
        :title="group.name + ' Closed'"
      >
        <ClockEndIcon title="" />
      </DashboardPanel>
    </div>

    <div class="row">
      <div class="col-lg-6">
        <BaseCard>
          <template #header>
            <font-awesome-icon icon="chart-bar" />
            {{ group.name }} Ticket By Category
          </template>
          <div>
            <BarChart
              :chart-data="categoryData"
              :options="categoryOptions"
              :width="null"
              :height="null"
            />
          </div>
        </BaseCard>
      </div>

      <div class="col-lg-6">
        <BaseCard>
          <template #header>
            <font-awesome-icon icon="chart-pie" />
            {{ group.name }} Ticket Status
          </template>
          <div>
            <DoughnutChart
              :chart-data="statusData"
              :options="statusOptions"
              :width="null"
              :height="null"
            />
          </div>
        </BaseCard>
      </div>
    </div>

    <div class="row">
      <DashboardPanel
        :colour="state.closed.colour"
        :count="percentCompleted + '%'"
        :title="state.closed.count + '/' + state.total.count + ' Closed'"
      >
        <CalendarMultipleCheckIcon title="" />
      </DashboardPanel>
      <DashboardPanel
        :colour="state.rejected.colour"
        :count="percentRejected + '%'"
        :title="state.rejected.count + '/' + state.total.count + ' Rejected'"
      >
        <ClipboardAlertIcon title="" />
      </DashboardPanel>
      <DashboardPanel
        :colour="state.open.colour"
        :count="percentOpen + '%'"
        :title="state.open.count + '/' + state.total.count + ' Opened'"
      >
        <CalendarClockIcon title="" />
      </DashboardPanel>
    </div>
  </div>
  <!-- /.content -->
</template>

<script>
/**
 * Main Dashboard view showing projects summaries with charts and graphs.
 */
import moment from 'moment'
import BarChart from '@/views/Report/BarChart.js'
import DoughnutChart from '@/views/Report/DoughnutChart.js'
import groupAPI from '@/api/group'
import reportAPI from '@/api/report'
import SigmaIcon from 'vue-material-design-icons/Sigma.vue'
import ClockStartIcon from 'vue-material-design-icons/ClockStart.vue'
import ClockFastIcon from 'vue-material-design-icons/ClockFast.vue'
import ClockEndIcon from 'vue-material-design-icons/ClockEnd.vue'
import CalendarMultipleCheckIcon from 'vue-material-design-icons/CalendarMultipleCheck.vue'
import ClipboardAlertIcon from 'vue-material-design-icons/ClipboardAlert.vue'
import CalendarClockIcon from 'vue-material-design-icons/CalendarClock.vue'
import ReportHeader from '@/views/Report/ReportHeader'
import DashboardPanel from '@/views/Report/DashboardPanel'

export default {
  components: {
    BarChart,
    DoughnutChart,
    SigmaIcon,
    ClockStartIcon,
    ClockFastIcon,
    ClockEndIcon,
    CalendarMultipleCheckIcon,
    ClipboardAlertIcon,
    CalendarClockIcon,
    ReportHeader,
    DashboardPanel,
  },
  data () {
    return {
      group: {
        name: 'IP Networks',
      },
      state: {
        total: { count: 0, colour: 'rgb(94,181,225)' },
        new: { count: 0, colour: 'rgb(242,146,38)' },
        rejected: { count: 0, colour: 'rgb(150,121,189)' },
        open: { count: 0, colour: 'rgb(240,178,196)' },
        closed: { count: 0, colour: 'rgb(165,216,87)' },
      },
      categoryDescr: {},
      defaultColours: [
        '#f39c12',
        '#e6805f',
        '#00a65a',
        '#b04012',
        '#11cc12',
        '#aa5511',
        '#f30012',
        '#ffce56',
        '#1fc8db',
        '#fce473',
        '#42afe3',
        '#ed6c63',
      ],
      categoryColours: {
        'NC1': 'rgb(39,104,169)',
        'EV1': 'rgb(242,212,116)',
        'NV1': 'rgb(121,189,157)',
        'NV2': 'rgb(210,80,80)',
        'O2': 'rgb(165,216,87)',
        'O3': 'rgb(242,146,38)',
        'O4': 'rgb(240,178,196)',
        'O5': 'rgb(150,121,189)',
        'F-Type': 'rgb(206,206,206)',
        'N1': 'rgb(190,220,227)',
      },
      stateColours: {
        'draft': 'rgb(210,80,80)',
        'new': 'rgb(242,146,38)',
        'assigned team': 'rgb(206,206,206)',
        'assigned tech lead/pm': 'rgb(242,212,116)',
        'reject1': 'rgb(150,121,189)',
        'quoted': 'rgb(39,104,169)',
        'open': 'rgb(240,178,196)',
        'pending closure': 'rgb(121,189,157)',
        'closed': 'rgb(165,216,87)',
      },
      categoryData: {
        labels: [],
        datasets: [],
      },
      categoryOptions: {
        onClick: this.onClickCategory,
        legend: {
          display: false,
        },
        scales: {
          yAxes: [{
            ticks: {
              beginAtZero: true,
              // skip non-integer ticks
              callback: function (value) { if (value % 1 === 0) { return value } },
            },
          }],
        },
        tooltips: {
          callbacks: {
            title: this.catTooltipTitle,
            label: this.tooltipLabel,
          },
        },
      },
      statusData: {
        labels: [],
        datasets: [],
      },
      statusOptions: {
        onClick: this.onClickStatus,
        legend: {
          position: 'bottom',
        },
        tooltips: {
          callbacks: {
            title: this.tooltipTitle,
            label: this.tooltipLabel,
          },
        },
      },
    }
  },
  computed: {
    percentCompleted () {
      if (this.state.total === 0 || this.state.total.count === 0) {
        return 0
      } else {
        return Math.round(this.state.closed.count / this.state.total.count * 100)
      }
    },
    percentRejected () {
      if (this.state.total === 0 || this.state.total.count === 0) {
        return 0
      } else {
        return Math.round(this.state.rejected.count / this.state.total.count * 100)
      }
    },
    percentOpen () {
      if (this.state.total === 0 || this.state.total.count === 0) {
        return 0
      } else {
        return Math.round(this.state.open.count / this.state.total.count * 100)
      }
    },
  },
  beforeRouteEnter (to, from, next) {
    if (!to.query || !to.query.startDate) {
      // set default start date to beginning of the year
      const startDate = moment().startOf('year').format('YYYY-MM-DD')
      next({ path: to.path, query: { ...to.query, startDate } })
    } else {
      next()
    }
  },
  beforeRouteUpdate (to, from, next) {
    if (!to.query || !to.query.startDate) {
      // set default start date to beginning of the year
      const startDate = moment().startOf('year').format('YYYY-MM-DD')
      next({ path: to.path, query: { ...to.query, startDate } })
    } else {
      next()
    }
  },
  methods: {
    getData () {
      const { groupId, teamId, startDate, endDate } = this.$route.query

      Promise.all([
        groupAPI.getGroup(groupId),
        reportAPI.getServiceSummaryReport(groupId, teamId, startDate, endDate),
        reportAPI.getStatusSummaryReport(groupId, teamId, startDate, endDate),
      ]).then(([group, barStats, pieStats]) => {
        this.group = group.data
        this.setBarStats(barStats.data)
        this.setPieStats(pieStats.data)
      }).catch(error => {
        console.log(error)
      })
    },
    onClickCategory (event, elements) {
      if (!elements || !elements[0]) {
        return
      }
      // add one day to end date so the end day is included
      const endDate = moment(this.$route.query.endDate).add(1, 'days').format('YYYY-MM-DD')
      const element = elements[0]
      const route = {
        name: 'projects',
        query: {
          project_type_name: this.group.name,
          created_on_gte: this.$route.query.startDate,
          created_on_lt: endDate,
        },
      }
      if (element._model.label === 'Unassigned') {
        route.query.services_is_null = ''
      } else {
        route.query.services_includes = element._model.label
      }
      this.$router.push(route)
    },
    onClickStatus (event, elements) {
      if (!elements || !elements[0]) {
        return
      }
      const element = elements[0]
      // add one day to end date so the end day is included
      const endDate = moment(this.$route.query.endDate).add(1, 'days').format('YYYY-MM-DD')
      this.$router.push({
        name: 'projects',
        query: {
          project_type_name: this.group.name,
          project_state_name: element._model.label,
          created_on_gte: this.$route.query.startDate,
          created_on_lt: endDate,
        },
      })
    },
    setBarStats (barStats) {
      const labels = []
      const data = []
      const colours = []
      const defaultColours = this.defaultColours.slice()
      this.categoryDescr = {}
      barStats.forEach(s => {
        labels.push(s.category)
        data.push(s.count)
        this.categoryDescr[s.category] = s.descr
        // find colour for category
        let colour = s.colour
        if (!colour) {
          colour = this.categoryColours[s.category]
        }
        if (!colour) {
          // no assigned colour, allocate one from default colours
          colour = defaultColours.pop()
        }
        colours.push(colour)
      })
      this.categoryData = {
        labels: labels,
        datasets: [{ data: data, backgroundColor: colours }],
      }
    },
    setPieStats (pieStats) {
      const labels = []
      const data = []
      const colours = []
      const defaultColours = this.defaultColours.slice()
      this.state.total.count = 0
      this.state.new.count = 0
      this.state.rejected.count = 0
      this.state.open.count = 0
      this.state.closed.count = 0
      pieStats.forEach(s => {
        this.state.total.count += s.count
        if (s.state === 'reject1' || s.state === 'reject2') {
          this.state.rejected.count += s.count
        }
        labels.push(this.capitalize(s.state))
        data.push(s.count)
        // find colour for state
        s.colour = s.colour || this.stateColours[s.state]
        if (!s.colour) {
          // no assigned colour, allocate one from default colours
          s.colour = defaultColours.pop()
        }
        colours.push(s.colour)
        this.state[s.state] = s
      })
      this.statusData = {
        labels: labels,
        datasets: [{ data: data, backgroundColor: colours }],
      }
    },
    capitalize (s) {
      return s.replace(/\b\w/g, l => l.toUpperCase())
    },
    tooltipTitle (tooltipItems, data) {
      const index = tooltipItems[0].index
      return data.labels[index]
    },
    catTooltipTitle (tooltipItems, data) {
      const index = tooltipItems[0].index
      return this.categoryDescr[data.labels[index]]
    },
    tooltipLabel (tooltipItem, data) {
      const dataset = data.datasets[tooltipItem.datasetIndex]
      const total = dataset.data.reduce(function (previousValue, currentValue, currentIndex, array) {
        return previousValue + currentValue
      })
      const currentValue = dataset.data[tooltipItem.index]
      const percentage = Math.floor(((currentValue / total) * 100) + 0.5)
      return ' ' + currentValue + ' (' + percentage + '%)'
    },
  },
}
</script>

<style lang="scss" scoped>
#dashboard {
  // fix size and alignment of icons
  .material-design-icon {
    font-size: 48px;
    bottom: 0.125em;
  }
}
</style>
